import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
} from "@/components/ui";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageHeader } from "@/components/ui/page-header";
import { productsApi } from "@/services/api";
import {
  Package,
  Plus,
  Pencil,
  Trash2,
  Star,
  Loader2,
  CheckCircle,
  XCircle,
  Search,
} from "lucide-react";

interface Product {
  id: string;
  name: string;
  description?: string;
  cdiRate: string;
  gracePeriodDays: number;
  dayBasis: "BUSINESS_252" | "CALENDAR_365";
  isDefault: boolean;
  status: "ACTIVE" | "INACTIVE";
  createdAt: string;
  updatedAt: string;
}

export function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmSetDefault, setConfirmSetDefault] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    cdiRate: "",
    gracePeriodDays: "0",
    dayBasis: "BUSINESS_252" as "BUSINESS_252" | "CALENDAR_365",
    isDefault: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Result feedback
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const data = await productsApi.list();
      setProducts(data);
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setFormData({
      name: "",
      description: "",
      cdiRate: "",
      gracePeriodDays: "0",
      dayBasis: "BUSINESS_252",
      isDefault: false,
    });
    setErrors({});
  }

  function openCreateModal() {
    resetForm();
    setShowCreateModal(true);
  }

  function openEditModal(product: Product) {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      description: product.description || "",
      cdiRate: product.cdiRate,
      gracePeriodDays: product.gracePeriodDays.toString(),
      dayBasis: product.dayBasis,
      isDefault: product.isDefault,
    });
    setErrors({});
    setShowEditModal(true);
  }

  function openDeleteConfirm(product: Product) {
    setSelectedProduct(product);
    setConfirmDelete(true);
  }

  function openSetDefaultConfirm(product: Product) {
    setSelectedProduct(product);
    setConfirmSetDefault(true);
  }

  function validateForm(): boolean {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length < 3) {
      newErrors.name = "Nome é obrigatório (mínimo 3 caracteres)";
    }

    if (!formData.cdiRate) {
      newErrors.cdiRate = "Taxa CDI é obrigatória";
    } else {
      const rate = parseFloat(formData.cdiRate);
      if (isNaN(rate) || rate <= 0 || rate > 200) {
        newErrors.cdiRate = "Taxa deve ser entre 0 e 200%";
      }
    }

    const gracePeriod = parseInt(formData.gracePeriodDays);
    if (isNaN(gracePeriod) || gracePeriod < 0) {
      newErrors.gracePeriodDays = "Carência deve ser >= 0 dias";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleCreate() {
    if (!validateForm()) return;

    setSaving(true);
    setResult(null);
    try {
      await productsApi.create({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        cdiRate: parseFloat(formData.cdiRate),
        gracePeriodDays: parseInt(formData.gracePeriodDays),
        dayBasis: formData.dayBasis,
        isDefault: formData.isDefault,
      });
      setShowCreateModal(false);
      resetForm();
      setResult({ success: true, message: "Produto criado com sucesso!" });
      await loadData();
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao criar produto",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate() {
    if (!validateForm() || !selectedProduct) return;

    setSaving(true);
    setResult(null);
    try {
      await productsApi.update(selectedProduct.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        cdiRate: parseFloat(formData.cdiRate),
        gracePeriodDays: parseInt(formData.gracePeriodDays),
        dayBasis: formData.dayBasis,
      });
      setShowEditModal(false);
      resetForm();
      setSelectedProduct(null);
      setResult({ success: true, message: "Produto atualizado com sucesso!" });
      await loadData();
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao atualizar produto",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!selectedProduct) return;

    setDeleting(true);
    try {
      await productsApi.delete(selectedProduct.id);
      setConfirmDelete(false);
      setSelectedProduct(null);
      setResult({ success: true, message: "Produto removido com sucesso!" });
      await loadData();
    } catch (error) {
      setResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao remover produto",
      });
    } finally {
      setDeleting(false);
    }
  }

  async function handleSetDefault() {
    if (!selectedProduct) return;

    setSettingDefault(true);
    try {
      await productsApi.setDefault(selectedProduct.id);
      setConfirmSetDefault(false);
      setSelectedProduct(null);
      setResult({
        success: true,
        message: `"${selectedProduct.name}" definido como produto padrão!`,
      });
      await loadData();
    } catch (error) {
      setResult({
        success: false,
        message:
          error instanceof Error ? error.message : "Erro ao definir como padrão",
      });
    } finally {
      setSettingDefault(false);
    }
  }

  // Filter products
  const filteredProducts = products.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.description?.toLowerCase().includes(search.toLowerCase())
  );

  // Stats
  const activeCount = products.filter((p) => p.status === "ACTIVE").length;
  const defaultProduct = products.find((p) => p.isDefault);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Package className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Produtos de Investimento"
          description="Configure os produtos disponíveis para as empresas"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Package className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Produtos de Investimento"
        description="Configure os produtos disponiveis para as empresas"
        stats={[
          { value: products.length, label: "Total" },
          { value: activeCount, label: "Ativos" },
          { value: defaultProduct ? "1" : "0", label: "Default" },
        ]}
        actions={
          <Button onClick={openCreateModal}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Produto
          </Button>
        }
      />

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar produtos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Result feedback */}
      {result && (
        <div
          className={`p-4 rounded-lg flex items-center gap-3 ${
            result.success
              ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {result.success ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{result.message}</span>
        </div>
      )}

      {/* Products Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lista de Produtos</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredProducts.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {search
                ? "Nenhum produto encontrado com esse filtro."
                : "Nenhum produto cadastrado ainda."}
            </p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-4 font-medium text-sm">Nome</th>
                    <th className="text-left p-4 font-medium text-sm">
                      Taxa CDI
                    </th>
                    <th className="text-left p-4 font-medium text-sm">
                      Carência
                    </th>
                    <th className="text-left p-4 font-medium text-sm">Base</th>
                    <th className="text-left p-4 font-medium text-sm">
                      Status
                    </th>
                    <th className="text-right p-4 font-medium text-sm">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-muted/50">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            {product.name}
                          </span>
                          {product.isDefault && (
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          )}
                        </div>
                        {product.description && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {product.description}
                          </p>
                        )}
                      </td>
                      <td className="p-4 text-sm font-medium">
                        {parseFloat(product.cdiRate).toFixed(2)}%
                      </td>
                      <td className="p-4 text-sm">
                        {product.gracePeriodDays} dias
                      </td>
                      <td className="p-4 text-sm">
                        {product.dayBasis === "BUSINESS_252" ? "252 DU" : "365 DC"}
                      </td>
                      <td className="p-4">
                        <Badge
                          variant={
                            product.status === "ACTIVE" ? "success" : "secondary"
                          }
                        >
                          {product.status === "ACTIVE" ? "Ativo" : "Inativo"}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          {!product.isDefault && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openSetDefaultConfirm(product)}
                              title="Definir como padrão"
                            >
                              <Star className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(product)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteConfirm(product)}
                            disabled={product.isDefault}
                            title={
                              product.isDefault
                                ? "Não é possível excluir o produto padrão"
                                : "Excluir"
                            }
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle>Novo Produto</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Nome</label>
                <Input
                  placeholder="Ex: Flexível"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  error={errors.name}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Descrição</label>
                <Input
                  placeholder="Ex: Produto sem carência"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Taxa CDI (%)</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="95.00"
                    value={formData.cdiRate}
                    onChange={(e) =>
                      setFormData({ ...formData, cdiRate: e.target.value })
                    }
                    error={errors.cdiRate}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Percentual do CDI (ex: 95 = 95% do CDI)
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Carência (dias)</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={formData.gracePeriodDays}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        gracePeriodDays: e.target.value,
                      })
                    }
                    error={errors.gracePeriodDays}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Base de Cálculo</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={formData.dayBasis}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      dayBasis: e.target.value as "BUSINESS_252" | "CALENDAR_365",
                    })
                  }
                >
                  <option value="BUSINESS_252">
                    252 Dias Úteis (padrão brasileiro)
                  </option>
                  <option value="CALENDAR_365">365 Dias Corridos</option>
                </select>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) =>
                    setFormData({ ...formData, isDefault: e.target.checked })
                  }
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <div>
                  <span className="text-sm font-medium">Produto padrão</span>
                  <p className="text-xs text-muted-foreground">
                    Será usado automaticamente para novas alocações
                  </p>
                </div>
              </label>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={handleCreate} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Criar Produto
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle>Editar Produto</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Nome</label>
                <Input
                  placeholder="Ex: Flexível"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  error={errors.name}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Descrição</label>
                <Input
                  placeholder="Ex: Produto sem carência"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Taxa CDI (%)</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="95.00"
                    value={formData.cdiRate}
                    onChange={(e) =>
                      setFormData({ ...formData, cdiRate: e.target.value })
                    }
                    error={errors.cdiRate}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Carência (dias)</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={formData.gracePeriodDays}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        gracePeriodDays: e.target.value,
                      })
                    }
                    error={errors.gracePeriodDays}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Base de Cálculo</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={formData.dayBasis}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      dayBasis: e.target.value as "BUSINESS_252" | "CALENDAR_365",
                    })
                  }
                >
                  <option value="BUSINESS_252">
                    252 Dias Úteis (padrão brasileiro)
                  </option>
                  <option value="CALENDAR_365">365 Dias Corridos</option>
                </select>
              </div>

              {selectedProduct.isDefault && (
                <p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                  Este é o produto padrão. Para alterar, defina outro produto
                  como padrão primeiro.
                </p>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedProduct(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button onClick={handleUpdate} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Salvar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
          setSelectedProduct(null);
        }}
        onConfirm={handleDelete}
        title="Excluir Produto"
        description={`Tem certeza que deseja excluir o produto "${
          selectedProduct?.name || ""
        }"? Esta ação não pode ser desfeita.`}
        variant="danger"
        loading={deleting}
      />

      {/* Set Default Confirmation */}
      <ConfirmDialog
        open={confirmSetDefault}
        onClose={() => {
          setConfirmSetDefault(false);
          setSelectedProduct(null);
        }}
        onConfirm={handleSetDefault}
        title="Definir como Padrão"
        description={`Deseja definir "${
          selectedProduct?.name || ""
        }" como produto padrão? Novas alocações usarão este produto automaticamente.`}
        loading={settingDefault}
      />
    </div>
  );
}
